<template>
  <section
    class="bootcamp-detail"
    :style="`background-image: url(${defaultBackground})`"
  >
    <div class="bootcamp-detail__overlay"></div>
    <div class="bootcamp-detail__wrapper">
      <div class="bootcamp-detail__header-wrapper">
        <h1 class="bootcamp-detail__header">
          <HeaderIcon class="bootcamp-detail__header--icon" /> BOOTCAMPS
        </h1>
      </div>
      <div v-if="isPageLoading" class="bootcamp-detail__loader">
        <LoadingSpinner />
      </div>
      <template v-else>
        <div class="bootcamp-detail__back-link-wrapper">
          <router-link
            :to="{ name: 'bootcamps' }"
            class="bootcamp-detail__back-link"
          >
            <span class="material-icons">keyboard_arrow_left</span>Back to
            Bootcamps</router-link
          >
        </div>
        <div class="bootcamp-detail__content">
          <div class="bootcamp-detail__main">
            <div class="bootcamp-detail__overview">
              <BootcampVideo
                v-if="isWatchingVideo"
                class="bootcamp-detail__video"
                v-bind="{
                  isVideoLoading: isActionLoading,
                  video,
                  videoStatus,
                }"
              />
              <BootcampOverviewCard
                v-else
                v-bind="{
                  bootcamp,
                  learningAreaName,
                  renderMarkdown,
                  hasAccess,
                  userHasRegistered,
                  isBootcampRegisterable,
                  canUpgradePlan,
                }"
                @handle-bootcamp-registration="handleToggleConfirmationModal"
                @handle-upgrade-plan-to-premium="handlePlanUpgrade"
              />
            </div>
            <div class="bootcamp-detail__list">
              <BootcampDetailsList v-bind="{ bootcamp, formatDate }" />
            </div>
            <aside
              class="bootcamp-detail__itinerary--mobile"
              v-if="contentStructureShouldBeVisible"
            >
              <ActionLoader
                v-if="isContentLoading"
                loaderClass="bootcamp-detail__itinerary--spinner"
              />
              <BootcampContent
                v-else
                v-bind="{
                  content: bootcamp.content,
                  hasAccess,
                  canUpgradePlan,
                }"
                @requestPlanUpgrade="handlePlanUpgrade"
              />
            </aside>
            <div class="bootcamp-detail__materials">
              <div class="bootcamp-detail__materials-content">
                <ActionLoader
                  v-if="isContentLoading"
                  loaderClass="bootcamp-detail__materials--spinner"
                />
                <BootcampMaterials
                  v-else
                  v-bind="{ bootcamp, files, hasAccess, canUpgradePlan }"
                  @requestPlanUpgrade="handlePlanUpgrade"
                />
              </div>
              <aside
                class="bootcamp-detail__materials-days"
                v-if="!contentStructureShouldBeVisible"
              >
                <BootcampDays v-bind="{ days: bootcamp.occurrences }" />
              </aside>
            </div>
          </div>
          <aside
            class="bootcamp-detail__itinerary--desktop"
            v-if="contentStructureShouldBeVisible"
          >
            <ActionLoader
              v-if="isContentLoading"
              loaderClass="bootcamp-detail__itinerary--spinner"
            />
            <BootcampContent
              v-else
              v-bind="{
                content: bootcamp.content,
                hasAccess,
                canUpgradePlan,
              }"
              @requestPlanUpgrade="handlePlanUpgrade"
            />
          </aside>
        </div>
        <div class="bootcamp-detail__instructors">
          <div
            class="bootcamp-detail__instructor"
            v-for="instructor in bootcamp.instructors"
            :key="instructor.id"
          >
            <BootcampInstructor v-bind="{ instructor }" />
          </div>
        </div>
        <div class="bootcamp-detail__related-courses">
          <template v-if="!isMobile">
            <CourseCarouselSection
              :tag="learningAreaName"
              :algoliaKeys="algoliaKeys"
              :isFreeTrialUser="isFreeTrialUser"
              customTitle="Related Courses"
              facet="learning_areas.name"
            />
          </template>
          <template v-else>
            <MobileCourseCarouselSection
              :tag="learningAreaName"
              :algoliaKeys="algoliaKeys"
              :isFreeTrialUser="isFreeTrialUser"
              customTitle="Related Courses"
              facet="learning_areas.name"
            />
          </template>
        </div>
        <BootcampConfirmationModal
          v-if="isConfirmationModalShown"
          v-bind="{
            bootcamp,
            learningAreaName,
            profile,
            isLoading: isActionLoading,
            formatDate,
          }"
          @confirm-btn-clicked="registerForBootcamp"
          @close-bootcamp-modal="handleToggleConfirmationModal"
        />
        <PremiumUpgradeModal
          v-if="isPremiumUpgradeModalShown"
          @toggle-premium-upgrade-modal="isPremiumUpgradeModalShown = false"
          :subscriptionId="planToBeUpgraded.id"
        />
      </template>
      <MediaDelegate
        v-if="isTakingQuiz"
        v-bind="{
          parentType: 'bootcamp',
          content: { id: currentQuizId, type: 'quiz' },
          contentTitle: quizTitle,
          userCompletionStatus: quizStatus,
          canStartIssue: false,
        }"
        @toggleQuiz="closeQuiz"
        @is-loading="toggleLoadingState"
      />
    </div>
  </section>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import HeaderIcon from '@/assets/icons/bootcamps/header_icon.svg'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import LoadingSpinner from '@/components/loaders/LoadingSpinner.vue'
import BootcampOverviewCard from '@/components/bootcamp/BootcampOverviewCard.vue'
import BootcampVideo from '@/components/bootcamp/BootcampVideo.vue'
import BootcampDetailsList from '@/components/bootcamp/BootcampDetailsList.vue'
import BootcampContent from '@/components/bootcamp/bootcamp-itinerary/Content.vue'
import BootcampMaterials from '@/components/bootcamp/BootcampMaterials.vue'
import BootcampInstructor from '@/components/bootcamp/BootcampInstructor.vue'
import BootcampConfirmationModal from '@/components/modals/BootcampConfirmationModal.vue'
import PremiumUpgradeModal from '@/components/modals/PremiumUpgradeModal.vue'
import CourseCarouselSkel from '@/components/loaders/skeletons/home-page/CourseCarouselSkel.vue'
import MobileCarouselSkel from '@/components/loaders/skeletons/home-page/MobileCarouselSkel.vue'
import MediaDelegate from '@/components/media/MediaDelegate.vue'
import BootcampDays from '@/components/bootcamp/bootcamp-itinerary/BootcampDays.vue'
import CONSTANTS from '@/constants'
import {
  formatDateForBootcamp,
  hasBaseAccess,
  lazyLoadComponent,
  hasElevatedRoles,
} from '@/helpers'

const marked = require('marked')

export default {
  name: 'Bootcamp',
  components: {
    HeaderIcon: { ...HeaderIcon, name: 'HeaderIcon' },
    ActionLoader,
    LoadingSpinner,
    BootcampOverviewCard,
    BootcampVideo,
    BootcampDetailsList,
    BootcampContent,
    BootcampMaterials,
    BootcampInstructor,
    BootcampConfirmationModal,
    PremiumUpgradeModal,
    MediaDelegate,
    BootcampDays,
    CourseCarouselSection: lazyLoadComponent({
      componentFactory: () =>
        import('@/components/home/subscriber-view/CourseCarousel'),
      loading: CourseCarouselSkel,
    }),
    MobileCourseCarouselSection: lazyLoadComponent({
      componentFactory: () =>
        import('@/components/home/subscriber-view/MobileCourseCarousel'),
      loading: MobileCarouselSkel,
    }),
  },
  data() {
    return {
      formatDate: formatDateForBootcamp,
      isConfirmationModalShown: false,
      planToBeUpgraded: {},
      isPremiumUpgradeModalShown: false,
      isMobile: false,
      defaultBackground:
        'https://s3.amazonaws.com/my.ine.com/assets/dashboard/dashboard_header.png',
    }
  },
  computed: {
    ...mapState({
      isAuthenticated: state => state.auth.isAuthenticated,
      isPageLoading: state => state.app.isPageLoading,
      isContentLoading: state => state.content.isContentLoading,
      isActionLoading: state => state.app.isComponentLoading,
      bootcamp: state => state.bootcamps.selectedBootcamp,
      profile: state => state.auth.userProfile,
      videos: state => state.content.currentVideos,
      quizzes: state => state.content.currentQuizzes,
      playingVideoId: state => state.content.playingVideoId,
      currentQuizId: state => state.content.currentQuizId,
      currentQuiz: state => state.content.currentQuiz,
      algoliaKeys: state => state.search.algoliaKeys,
    }),
    ...mapGetters({
      usersSubscriptions: 'subscriptions/usersSubscriptions',
      isFreeTrialUser: 'auth/isFreeTrialUser',
      contentFiles: 'files/getCurrentFilesById',
    }),
    isWatchingVideo() {
      return this.playingVideoId && this.playingVideoId.length
    },
    isTakingQuiz() {
      return this.currentQuizId && this.currentQuizId.length
    },
    video() {
      if (this.isWatchingVideo) {
        return this.videos.find(vid => vid.id === this.playingVideoId)
      }
      return null
    },
    videoStatus() {
      if (this.video) {
        const {
          video: { user_status },
        } = this
        return user_status
      }
      return CONSTANTS.CONTENT_USER_STATUS.UNSTARTED
    },
    quizTitle() {
      if (this.currentQuiz) {
        const {
          currentQuiz: { name, metadata },
        } = this
        return metadata?.quiz_title ? metadata.quiz_title : name
      }
      return 'Bootcamp Quiz'
    },
    quizStatus() {
      if (this.currentQuiz) {
        const {
          currentQuiz: { user_status },
        } = this
        return user_status === CONSTANTS.CONTENT_USER_STATUS.FINISHED
      }
      return CONSTANTS.CONTENT_USER_STATUS.UNSTARTED
    },
    hasAccess() {
      const { bootcamp } = this
      return hasBaseAccess(bootcamp)
    },
    canUpgradePlan() {
      const {
        usersSubscriptions: { personalPlans },
      } = this
      return !!(personalPlans && personalPlans.length)
    },
    userHasRegistered() {
      const {
        bootcamp: {
          user_status: { registered },
        },
      } = this
      return registered || false
    },
    isBootcampRegisterable() {
      const {
        bootcamp: { registration_status },
      } = this
      return registration_status === CONSTANTS.BOOTCAMP_REGISTRATION_STATUS.OPEN
    },
    learningAreaName() {
      const {
        bootcamp: { learning_areas },
      } = this
      return learning_areas && learning_areas[0]
        ? learning_areas[0].name
        : 'IT Essentials'
    },
    contentStructureShouldBeVisible() {
      const {
        bootcamp: { status, content },
      } = this
      return (
        (status === CONSTANTS.BOOTCAMP_CONTENT_STATUS.CONTENT_PUBLISHED ||
          hasElevatedRoles()) &&
        content &&
        content.length
      )
    },
    files() {
      const {
        bootcamp: { files },
      } = this
      return this.contentFiles(files)
    },
  },
  watch: {
    windowWidth(width) {
      this.isMobile = width <= 768
    },
  },
  methods: {
    handleToggleConfirmationModal() {
      this.isConfirmationModalShown = !this.isConfirmationModalShown
    },
    handlePlanUpgrade() {
      const {
        usersSubscriptions: { personalPlans },
      } = this
      this.planToBeUpgraded = personalPlans[0]
      this.isPremiumUpgradeModalShown = true
    },
    renderMarkdown(string) {
      return marked(string)
    },
    async registerForBootcamp() {
      this.$store.dispatch('app/setIsComponentLoading', true)
      const {
        bootcamp: { id },
      } = this
      await this.$store.dispatch('bootcamps/registerForBootcamp', id)
      await this.$store.dispatch('bootcamps/getBootcampById', id)
      this.handleToggleConfirmationModal()
      this.$store.dispatch('app/setIsComponentLoading', false)
    },
    async fetchBootcampFiles() {
      const {
        bootcamp: { files },
      } = this
      if (files && files.length) {
        await this.$store.dispatch('files/getFiles', files)
      }
    },
    async fetchBootcampContent() {
      const {
        bootcamp: { content },
      } = this
      if (content && content.length) {
        this.$store.dispatch('content/fetchContentForBootcamp', {
          content,
          isAuthenticated: this.isAuthenticated,
        })
      }
    },
    toggleLoadingState(state) {
      this.$store.dispatch('app/setIsComponentLoading', state)
    },
    closeQuiz() {
      this.$store.dispatch('content/setCurrentQuizId', '')
    },
  },
  async created() {
    this.$store.dispatch('app/setIsComponentLoading', false)
    this.$store.dispatch('app/setIsPageLoading', true)
    this.$store.dispatch('content/setIsContentLoading', true)
    const {
      $route: {
        params: { id },
      },
    } = this
    await this.$store.dispatch('bootcamps/getBootcampById', id)
    this.$store.dispatch('app/setIsPageLoading', false)
    if (this.hasAccess) {
      await this.fetchBootcampFiles()
    }
    await this.fetchBootcampContent()
    this.$store.dispatch('content/setIsContentLoading', false)
  },
  mounted() {
    this.isMobile = window.innerWidth <= 768
    this.$nextTick(() => {
      window.addEventListener('resize', () => {
        this.windowWidth = window.innerWidth
      })
    })
  },
  destroyed() {
    this.$store.dispatch('content/setPlayingVideoId', '')
    this.$store.dispatch('content/setCurrentQuizId', '')
    window.removeEventListener('resize', () => {
      this.windowWidth = window.innerWidth
    })
  },
}
</script>

<style lang="scss">
.bootcamp-detail {
  z-index: 1;
  position: relative;
  background-repeat: no-repeat;
  background-size: 100% auto;
  &__wrapper {
    margin: 0 auto;
    max-width: 1920px;
    padding: 125px 25px 50px 25px;
    @include tablet {
      padding: 125px 50px 150px 50px;
    }
    @include desktop {
      padding: 125px 150px 150px 150px;
    }
  }
  &__overlay {
    position: absolute;
    min-height: 100%;
    width: 100%;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 0.7),
      rgba(0, 0, 0, 0.8),
      rgba(0, 0, 0, 0.8),
      var(--dark-gray)
    );
    right: 0;
    top: 0;
    z-index: -1;
  }
  &__header-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  &__header {
    display: flex;
    align-items: center;
    background-color: var(--darker-gray);
    padding: 10px 30px;
    border-radius: 100px;
    margin: 0;
    font-size: rem(13px);
    color: var(--white);
    &--icon {
      margin: 3px 15px 0 0;
    }
  }
  &__back-link-wrapper {
    margin: 25px 0;
    @include desktop {
      margin: 25px 0 25px 45px;
    }
  }
  &__back-link {
    color: var(--white);
    text-decoration: underline;
    &:hover {
      color: var(--orange);
    }
  }
  &__content {
    display: flex;
    flex-wrap: wrap;
    @include tablet {
      flex-wrap: nowrap;
    }
  }
  &__main {
    flex-basis: 100%;
    @include desktop {
      flex-basis: 70%;
      padding: 0 30px;
      flex-grow: 1;
      padding: 0 50px;
    }
  }
  &__overview {
    margin: 0 0 50px 0;
  }
  &__list {
    margin: 0 0 50px 0;
  }
  &__materials {
    margin: 0 0 150px 0;
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    justify-content: flex-start;
    align-items: stretch;
    &--spinner {
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 100px auto;
    }
    &-content {
      flex-basis: 100%;
    }
    &-days {
      flex-basis: 100%;
    }
    @include desktop {
      flex-wrap: nowrap;
      &-content {
        flex-basis: 65%;
        flex-grow: 1;
      }
      &-days {
        flex-basis: 35%;
        margin-left: 35px;
        ul {
          padding-left: 20px;
        }
      }
    }
  }
  &__itinerary {
    &--spinner {
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto;
    }
    &--mobile {
      width: 100%;
      margin: 0 0 50px 0;
      @include desktop {
        display: none;
      }
    }
    &--desktop {
      display: none;
      @include desktop {
        display: block;
        flex-basis: 30%;
        max-width: 450px;
      }
    }
  }
  &__instructors {
    width: 100%;
    @include tablet {
      padding: 0 50px;
    }
  }
  &__instructor {
    width: 100%;
    margin-bottom: 80px;
    @include tablet {
      padding: 0 50px;
    }
  }
  &__related-courses {
    margin-top: 5rem;
  }
}
</style>
